import { useAutoneTranslation } from '@autone/translations';
import { useEffect, useState } from 'react';

import { DisabledReasonIcon } from '../../components/autone-multi-select/DisabledReasonIcon';
import Image from '../../components/image/Image';
import { type CoreProduct } from '../../redux/services';
import { Multiselect } from '../../tailwind/multiselect-inputs';
import { RippleRoot } from '../../tailwind/RippleRoot';

import { SEPARATOR } from './utilities';

export const isProductDisabled = (product: CoreProduct): boolean => {
  return !!product.parent_product_id;
};

export const MultiselectItemWithId = <
  T extends { id: string; description: string | null },
>({
  id,
  description,
}: T) => (
  <Multiselect.Item
    optionId={`${id}${SEPARATOR}${description}`}
    className="relative flex flex-row items-center gap-2 px-3 py-2 hover:bg-primary-5 cursor-pointer data-[selected=true]:bg-primary-10"
  >
    <Multiselect.ItemCheckbox />
    <div className="flex flex-col">
      <p>{description}</p>
      <p className="text-xs text-grey-60">{id}</p>
    </div>
    <RippleRoot
      className="bg-primary-60"
      pulseDuration={0.55}
      pulseOpacity={0.3}
    />
  </Multiselect.Item>
);

export const MultiselectProductItem = (product: CoreProduct) => {
  const { id, style_desc, image_url, parent_product_id } = product;
  const { common } = useAutoneTranslation();
  const disabled = isProductDisabled(product);
  const disabledReason = disabled
    ? common('substituted-product', {
        productId: parent_product_id,
      })
    : null;

  const [tooltipContainer, setTooltipContainer] = useState<Element | null>();

  useEffect(() => {
    const modalWrapper = document.querySelector(
      '[data-radix-popper-content-wrapper]',
    );
    setTooltipContainer(modalWrapper || undefined);
  }, []);

  return (
    <Multiselect.Item
      optionId={id}
      className="relative flex flex-row items-center justify-between gap-4 px-3 py-2 hover:bg-primary-5 cursor-pointer data-[selected=true]:bg-primary-10"
      disabled={disabled}
    >
      <div className="flex flex-row items-center">
        <Image
          alt={id}
          src={image_url}
          style={{
            width: 64,
            height: 64,
            objectFit: 'contain',
            marginRight: 1,
          }}
        />
        <div className="flex flex-col">
          <p>{style_desc}</p>
          <p className="text-xs text-grey-60">{id}</p>
        </div>
        {!disabled && (
          <RippleRoot
            className="bg-primary-60"
            pulseDuration={0.55}
            pulseOpacity={0.3}
          />
        )}
      </div>
      {disabled && disabledReason && (
        <DisabledReasonIcon
          disabledReason={disabledReason}
          container={tooltipContainer ?? undefined}
        />
      )}
    </Multiselect.Item>
  );
};
